import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  FilterOptions,
  GiftcardPlan,
  GiftcardPlanResponse,
  GiftcardPlanState,
} from "./types/gift-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: GiftcardPlanState = {
  giftcardPlans: [],
  giftcardPlan: null,
  selectedGiftcardPlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create GiftcardPlan
export const createGiftcardPlan = createAsyncThunk<
  GiftcardPlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("giftcardPlans/create", async (giftcardPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/gift-cards`, giftcardPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // 'Content-Type': 'application/json' do not dd this line, allow browser to handle
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create gift card plan" },
    );
  }
});

// ✅ Fetch GiftcardPlans (Paginated)
export const fetchGiftcardPlans = createAsyncThunk<
  GiftcardPlanResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("giftcardPlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 15,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/gift-cards`, {
        params,
        headers,
      });
    }
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch gift card plans" },
    );
  }
});

// ✅ Fetch Single GiftcardPlan by ID
export const fetchGiftPlanById = createAsyncThunk<
  GiftcardPlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("giftcardPlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/gift-cards/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch gift card plan" },
    );
  }
});

// ✅ Delete DataPlan
export const deleteGiftcardPlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("giftcardPlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/gift-cards/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete gift card plan" },
    );
  }
});

// ✅ Update DataPlan
export const updateGiftcardPlan = createAsyncThunk<
  GiftcardPlan,
  { id: number; data: Partial<GiftcardPlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("giftcardPlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/gift-cards/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update gift card plan" },
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("giftcardPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/gift-cards/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

// ✅ Slice
const giftcardPlanSlice = createSlice({
  name: "giftcardPlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetGiftcardPlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedGiftcardPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createGiftcardPlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createGiftcardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.giftcardPlan) {
          state.giftcardPlans.unshift(action.payload.giftcardPlan);
        }
      })
      .addCase(createGiftcardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create gift card plan";
      })

      // Fetch All
      .addCase(fetchGiftcardPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGiftcardPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.giftcardPlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchGiftcardPlans.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch gift card plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchGiftPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedGiftcardPlan = null;
      })
      .addCase(fetchGiftPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGiftcardPlan = action.payload;
      })
      .addCase(fetchGiftPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch gift card plan";
      })

      // Delete
      .addCase(deleteGiftcardPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGiftcardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.giftcardPlans = state.giftcardPlans.filter(
          (plan) => plan.id !== action.payload,
        );
      })
      .addCase(deleteGiftcardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete gift card plan";
      })

      // Update
      .addCase(updateGiftcardPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGiftcardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.giftcardPlans = state.giftcardPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan,
        );

        // Update selected data plan if it's the one updated
        if (state.selectedGiftcardPlan?.id === action.payload.id) {
          state.selectedGiftcardPlan = action.payload;
        }
      })
      .addCase(updateGiftcardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update gift card plan";
      });
  },
});

export const { setFilters, clearFilters, resetGiftcardPlanState } =
  giftcardPlanSlice.actions;
export default giftcardPlanSlice.reducer;
